import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Event } from '../types/event.types';

export interface EventWithDistance extends Event {
  distanceKm: number;
}

export const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatDistance = (km: number): string => {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
};

export const openInMaps = (lat: number, lng: number, address: string): void => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const encoded = encodeURIComponent(address);
  if (isIOS) {
    window.open(`https://maps.apple.com/?q=${encoded}&ll=${lat},${lng}`, '_blank');
  } else {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  }
};

export const searchEvents = async (
  searchText: string,
  userLat?: number,
  userLng?: number,
  radiusKm: number | null = null
): Promise<{ events: EventWithDistance[]; isGlobal: boolean }> => {
  const q = query(
    collection(db, 'activities'),
    where('status', '==', 1),
    where('published', '==', true)
  );

  const snapshot = await getDocs(q);
  const now = new Date();
  let events: EventWithDistance[] = [];

  snapshot.forEach(doc => {
    const data = doc.data();
    if (data.expired === true) return;

    const eventDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
    if (eventDate < now) return;

    const lat = Number(data.lat) || 0;
    const lng = Number(data.long) || 0;
    const distanceKm =
      userLat !== undefined && userLng !== undefined && lat !== 0 && lng !== 0
        ? haversineDistance(userLat, userLng, lat, lng)
        : 0;

    events.push({ id: doc.id, ...data, lat, long: lng, distanceKm } as EventWithDistance);
  });

  if (searchText.trim()) {
    const term = searchText.toLowerCase().trim();
    events = events.filter(e =>
      e.information?.toLowerCase().includes(term) ||
      e.information2?.toLowerCase().includes(term) ||
      (e.searchKeywords as string[] | undefined)?.some(k => k.toLowerCase().includes(term))
    );
  }

  const hasLocation = userLat !== undefined && userLng !== undefined;

  // Filtro por radio: solo si el usuario compartió su ubicación Y se eligió un radio específico
  if (radiusKm !== null && hasLocation) {
    events = events.filter(e => e.distanceKm <= radiusKm);
    // Sin fallback global: si no hay resultados en el radio, queda vacío
  }

  events.sort((a, b) => {
    if (hasLocation) return a.distanceKm - b.distanceKm;
    const tA = (a.date as any)?.toMillis?.() ?? 0;
    const tB = (b.date as any)?.toMillis?.() ?? 0;
    return tA - tB;
  });

  return { events, isGlobal: false };
};
